import { Badge } from '@/components/ui/Badge';
import { CardBox } from '@/components/ui/CardBox';
import { Icon } from '@/components/ui/Icon';
import {
  mdiAccountGroup,
  mdiBell,
  mdiBullhorn,
  mdiCalendarClock,
} from '@mdi/js';
import cx from 'classnames';
import { DateTime } from 'luxon';
import * as React from 'react';
import type { Notification, NotificationType } from '../hooks/useNotifications';

interface NotificationsTableProps {
  notifications: Notification[];
  isLoading: boolean;
}

const typeLabels: Record<NotificationType, string> = {
  plan_reminder: 'Plan Reminder',
  plan_created_by_admin: 'Plan Created',
  review_reminder: 'Review Reminder',
  announcement: 'Announcement',
  marketing: 'Marketing',
  system: 'System',
};

const typeColors: Record<
  NotificationType,
  'blue' | 'green' | 'yellow' | 'purple' | 'gray'
> = {
  plan_reminder: 'blue',
  plan_created_by_admin: 'green',
  review_reminder: 'yellow',
  announcement: 'purple',
  marketing: 'purple',
  system: 'gray',
};

const typeIcons: Record<NotificationType, string> = {
  plan_reminder: mdiCalendarClock,
  plan_created_by_admin: mdiCalendarClock,
  review_reminder: mdiCalendarClock,
  announcement: mdiBullhorn,
  marketing: mdiBullhorn,
  system: mdiBell,
};

export const NotificationsTable: React.FC<NotificationsTableProps> = ({
  notifications,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <CardBox>
        <div className='p-8 text-center'>
          <div className='animate-pulse space-y-4'>
            {[1, 2, 3].map(i => (
              <div key={i} className='h-16 bg-grey-100 rounded-lg' />
            ))}
          </div>
        </div>
      </CardBox>
    );
  }

  if (notifications.length === 0) {
    return (
      <CardBox>
        <div className='p-8 text-center text-grey-500'>
          <Icon path={mdiBell} size={48} className='mx-auto mb-4 opacity-50' />
          <p>No notifications sent yet</p>
        </div>
      </CardBox>
    );
  }

  return (
    <CardBox hasTable>
      <table className='min-w-full divide-y divide-grey-200'>
        <thead className='bg-grey-50'>
          <tr>
            <th className='px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider'>
              Notification
            </th>
            <th className='px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider'>
              Recipients
            </th>
            <th className='px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider'>
              Type
            </th>
            <th className='px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider'>
              Delivery
            </th>
            <th className='px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider'>
              Sent At
            </th>
          </tr>
        </thead>
        <tbody className='bg-white divide-y divide-grey-200'>
          {notifications.map(notification => (
            <tr key={notification.id} className='hover:bg-grey-50'>
              <td className='px-6 py-4'>
                <div className='flex items-start space-x-3'>
                  <div
                    className={cx(
                      'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
                      {
                        'bg-coffice-blue/10 text-coffice-blue':
                          typeColors[notification.type] === 'blue',
                        'bg-green-100 text-green-600':
                          typeColors[notification.type] === 'green',
                        'bg-yellow-100 text-yellow-600':
                          typeColors[notification.type] === 'yellow',
                        'bg-purple-100 text-purple-600':
                          typeColors[notification.type] === 'purple',
                        'bg-gray-100 text-gray-600':
                          typeColors[notification.type] === 'gray',
                      },
                    )}
                  >
                    <Icon path={typeIcons[notification.type]} size={20} />
                  </div>
                  <div>
                    <p className='text-sm font-medium text-grey-900'>
                      {notification.title}
                    </p>
                    <p className='text-sm text-grey-500 line-clamp-2'>
                      {notification.body}
                    </p>
                  </div>
                </div>
              </td>
              <td className='px-6 py-4'>
                <div className='flex items-center space-x-2'>
                  <Icon
                    path={mdiAccountGroup}
                    size={18}
                    className='text-grey-400'
                  />
                  <span className='text-sm font-medium text-grey-900'>
                    {notification.recipientCount ?? 1} users
                  </span>
                </div>
              </td>
              <td className='px-6 py-4'>
                <Badge color={typeColors[notification.type]} small>
                  {typeLabels[notification.type]}
                </Badge>
              </td>
              <td className='px-6 py-4'>
                <div className='space-y-1'>
                  <div className='flex items-center text-xs'>
                    <span className='text-grey-500 w-12'>Push:</span>
                    <span className='text-grey-900'>
                      {notification.pushSentCount ??
                        (notification.isPushSent ? 1 : 0)}
                      /{notification.recipientCount ?? 1}
                    </span>
                  </div>
                  <div className='flex items-center text-xs'>
                    <span className='text-grey-500 w-12'>Read:</span>
                    <span className='text-grey-900'>
                      {notification.readCount ?? (notification.isRead ? 1 : 0)}/
                      {notification.recipientCount ?? 1}
                    </span>
                  </div>
                </div>
              </td>
              <td className='px-6 py-4 text-sm text-grey-500'>
                {notification.createdAt
                  ? DateTime.fromISO(notification.createdAt).toFormat(
                      'MMM d, yyyy HH:mm',
                    )
                  : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardBox>
  );
};
